import type { ProjectSummary } from '@shared/api';
import { ROLE_FAMILIES } from '@shared/custom-attributes';
import { PEOPLE_SORT_FIELDS, PEOPLE_SORT_LABELS, type PeopleSortField, type PersonQueryResponse } from '@shared/people';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ExternalLink, Mail, Phone, Search, Trash2, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Alert, Badge, EmptyState, Spinner } from '../../components/ui/feedback';
import { Input, Select } from '../../components/ui/form';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';

const PAGE_SIZE = 50;

const ROLE_OPTIONS = Object.keys(ROLE_FAMILIES);

function roleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
}

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * People found at the collected businesses.
 *
 * Every person carries the page they were found on and how the value was obtained, because a
 * name and a job title with no provenance is exactly the kind of data nobody should act on.
 */
export function PeoplePage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project') ?? undefined;
  const client = useQueryClient();

  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [contact, setContact] = useState<'any' | 'email' | 'phone'>('any');
  const [sort, setSort] = useState<PeopleSortField>('fullName');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebounced(search);

  const projects = useQuery({ queryKey: queryKeys.projects, queryFn: () => api.get<ProjectSummary[]>('/api/projects') });

  const body = useMemo(
    () => ({
      ...(projectId ? { projectId } : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(role ? { roles: [role] } : {}),
      ...(contact === 'email' ? { hasEmail: true } : {}),
      ...(contact === 'phone' ? { hasPhone: true } : {}),
      sort,
      dir,
      offset,
      limit: PAGE_SIZE,
    }),
    [projectId, debouncedSearch, role, contact, sort, dir, offset],
  );

  const results = useQuery({
    queryKey: queryKeys.people(body),
    queryFn: () => api.post<PersonQueryResponse>('/api/people/query', body),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/people/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['people'] });
    },
  });

  const rows = results.data?.items ?? [];
  const total = results.data?.total ?? 0;

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <header className="mb-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">People</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          {total > 0 ? (
            <>
              <span className="tabular">{total.toLocaleString()}</span> people found on the websites of the businesses in
              your research.
            </>
          ) : (
            'Contacts found while visiting company websites.'
          )}
        </p>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            className="pl-8"
            value={search}
            placeholder="Search name, job title, email or company"
            aria-label="Search people"
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Project</span>
          <Select
            className="h-9 w-48"
            value={projectId ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setParams(value ? { project: value } : {});
              setOffset(0);
            }}
          >
            <option value="">All projects</option>
            {projects.data?.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Role</span>
          <Select
            className="h-9 w-40"
            value={role}
            onChange={(event) => {
              setRole(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">Any role</option>
            {ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {roleLabel(option)}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Has</span>
          <Select
            className="h-9 w-36"
            value={contact}
            onChange={(event) => {
              setContact(event.target.value as 'any' | 'email' | 'phone');
              setOffset(0);
            }}
          >
            <option value="any">Anything</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </Select>
        </label>

        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Sort by</span>
          <Select
            className="h-9 w-40"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value as PeopleSortField);
              setOffset(0);
            }}
          >
            {PEOPLE_SORT_FIELDS.map((field) => (
              <option key={field} value={field}>
                {PEOPLE_SORT_LABELS[field]}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant="outline"
            aria-label={dir === 'asc' ? 'Sort descending' : 'Sort ascending'}
            icon={dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
            onClick={() => setDir((current) => (current === 'asc' ? 'desc' : 'asc'))}
          >
            {dir === 'asc' ? 'Asc' : 'Desc'}
          </Button>
        </label>
      </div>

      {remove.error ? (
        <Alert className="mb-2" tone="error">
          {errorMessage(remove.error)}
        </Alert>
      ) : null}

      <Card className="overflow-hidden">
        {results.isPending ? (
          <div className="flex items-center gap-2 px-4 py-10 text-sm text-muted-foreground">
            <Spinner /> Loading people…
          </div>
        ) : results.isError ? (
          <Alert tone="error" className="m-3">
            {errorMessage(results.error)}
          </Alert>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title={debouncedSearch || role ? 'No people match these filters' : 'No people yet'}
            description={
              debouncedSearch || role
                ? 'Try clearing a filter to see everyone.'
                : 'People are found while visiting company websites. Turn on website enrichment in a research run to collect them.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[length:var(--text-table)]">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                    Job title
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                    Company
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                    Contact
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                    Found on
                  </th>
                  <th scope="col" className="w-10 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((person) => (
                  <tr key={person.id} className="border-b border-border transition-colors duration-150 last:border-b-0 hover:bg-muted/50">
                    <td className="max-w-56 truncate px-3 py-1.5 font-medium text-foreground">
                      {person.fullName}
                      {person.roles.length > 0 ? (
                        <span className="ml-1.5 inline-flex gap-1">
                          {person.roles.slice(0, 2).map((entry) => (
                            <Badge key={entry} tone="neutral">
                              {roleLabel(entry)}
                            </Badge>
                          ))}
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-56 truncate px-3 py-1.5 text-foreground">
                      {person.title ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="max-w-56 truncate px-3 py-1.5 text-foreground">
                      {person.companyName ?? <span className="text-muted-foreground">—</span>}
                      {person.companyCity ? <span className="text-muted-foreground"> · {person.companyCity}</span> : null}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="flex items-center gap-2">
                        {person.email ? (
                          <a className="flex items-center gap-1 text-accent hover:underline" href={`mailto:${person.email}`}>
                            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                            <span className="max-w-40 truncate">{person.email}</span>
                          </a>
                        ) : null}
                        {person.phone ? (
                          <span className="tabular flex items-center gap-1 text-foreground">
                            <Phone className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                            {person.phone}
                          </span>
                        ) : null}
                        {!person.email && !person.phone ? <span className="text-muted-foreground">—</span> : null}
                      </span>
                    </td>
                    <td className="max-w-56 truncate px-3 py-1.5">
                      {person.sourceUrl ? (
                        <a
                          className="flex items-center gap-1 text-accent underline-offset-4 hover:underline"
                          href={person.sourceUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span className="truncate">{person.sourceUrl.replace(/^https?:\/\//, '')}</span>
                        </a>
                      ) : (
                        <span className="text-muted-foreground">{person.source}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <button
                        type="button"
                        className="text-muted-foreground transition-colors duration-150 hover:text-destructive"
                        aria-label={`Remove ${person.fullName}`}
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(person.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > PAGE_SIZE ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="tabular text-[13px] text-muted-foreground">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
          </p>
          <span className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
